import * as Yup from 'yup'
import React, { useContext, useEffect, useState } from 'react'
import { Cell, Grid } from 'react-foundation'
import { Formik, Form, FormikHelpers } from 'formik'
import UserContext, { UserProvider } from '../../UserContext'
import { ApiFetch, ApiPut } from '../Common/Api'
import PageHeading from '../Common/PageHeading'
import { County } from '../../models/User'
import SubmitButton, { SubmitMessage } from '../Common/SubmitButton'
import { FocusOnFirstError } from '../Common/Input/FormFields'
import { TrackedButton } from '../Common/AnalyticsEvents'
import BusinessInformation from './BusinessInformation'
import ServiceTypes from './ServiceTypes'
import { ProviderCommunicationPreferences } from './CommunicationPreferences'
import ServiceArea from './ServiceArea'
import AdditionalNotificationContacts from './AdditionalNotificationContacts'
import { ProviderPreferencesForm, ProviderPreferencesPayload, UserProviderPreferences } from '../../models/UserPreferences'
import { CountyToZipMap, getStateServiceAreaData, normalizeState, RegionToCountyMap } from './stateServiceAreaConfig'

type Regions = Record<string, Record<string, string[]>>;
const FALLBACK_REGION_NAME = 'Configured Service Area'

const getCountyZipList = (county?: County) => {
    if (!county) return []
    if (county.zipCodesList && county.zipCodesList.length > 0) return county.zipCodesList
    if (county.zipCodes) {
        return county.zipCodes
            .split(',')
            .map(z => z.trim())
            .filter(Boolean)
    }
    return []
}

const hasCanonicalStateData = (regionToCounty: Record<string, string[]>, countyToZip: Record<string, number[]>) => {
    return Object.keys(regionToCounty).length > 0 && Object.keys(countyToZip).length > 0
}

const buildRegions = (regionToCounty: Record<string, string[]>, countyToZip: Record<string, number[]>, counties?: County[]): Regions => {
    const canonicalData = hasCanonicalStateData(regionToCounty, countyToZip)
    const seed = Object.keys(regionToCounty).reduce((acc: Regions, regionName: string) => {
        acc[regionName] = {};
        for (let countyName of regionToCounty[regionName]) {
            if (regionName.includes(':')) {
                countyName = `${countyName}:${regionName.split(':')[1]}`
            }
            if (!(countyName in countyToZip)) {
                // eslint-disable-next-line no-console
                console.log(countyName)
                acc[regionName][countyName] = []
                continue
            }
            acc[regionName][countyName] = countyToZip[countyName].map(z => z.toString());
        }
        return acc;
    }, {} as Regions)

    for (const county of counties || []) {
        const countyName = county.countyName
        if (!countyName) continue

        const existingRegion = Object.entries(seed).find(([, regionCounties]) => countyName in regionCounties)?.[0]
        if (existingRegion) continue

        // If this state has a canonical county map (OH/MI), suppress unknown counties to avoid
        // mixing stale cross-state data into the service-area UI.
        if (canonicalData)
            continue

        if (!seed[FALLBACK_REGION_NAME]) seed[FALLBACK_REGION_NAME] = {}
        seed[FALLBACK_REGION_NAME][countyName] = getCountyZipList(county)
    }

    return seed
}


const validationScheme = Yup.object({
    preferredPhoneNumber: Yup.string()
    .when('communicationMethods', {
      is: (methods: string[]) =>
        Array.isArray(methods) &&
        (methods.includes('Text Message')),
      then: Yup.string()
        .required('Preferred Phone Number is required')
        .matches(/^\(?\d{3}[) -.]*\d{3}[-.]?\d{4}$/, 'Invalid Phone ###-###-####'),
      otherwise: Yup.string().matches(/^\(?\d{3}[) -.]*\d{3}[-.]?\d{4}$/, 'Invalid Phone ###-###-####'),
    }),
    additionalContacts: Yup.array().of(Yup.object({
        email: Yup.string().email('Invalid email'),
        phoneNumber: Yup.string().matches(/^\(?\d{3}[) -.]*\d{3}[-.]?\d{4}$/, 'Invalid Phone ###-###-####'),
    }))
})

const ProviderPreferenceForm: React.FC = () => {
    const { user, setUser, cacheDelete } = useContext(UserContext)
    const [preferences, setPreferences] = useState<UserProviderPreferences>()
    const [loading, setLoading] = useState<boolean>(true)
    const activeStates = preferences?.authorizedStates;
    const normalizedActiveStates = activeStates?.map(s => normalizeState(s))
    const countyDerivedState = preferences?.counties?.find(c => !!c.state)?.state
    const activeState = normalizeState(countyDerivedState || preferences?.state)
    let { countyToZip, regionToCounty, zipToLatLng } = getStateServiceAreaData(activeState)
    const canonicalData = hasCanonicalStateData(regionToCounty, countyToZip)
    // Use preferences.counties directly for initial values to ensure latest data from backend
    let counties = preferences?.counties?.filter(c => normalizeState(c.state || activeState) === activeState);
    if (normalizedActiveStates && normalizedActiveStates.length >= 2) {
        //multi state logic quarantine. This component should be reworked, but here's a
        //short term solution to the multi state problem for now. It's designed in a way that
        //it shouldn't impact users not authorized in multiple states, and the risk of regressions should
        //mostly be for visual elements.  
        counties = preferences?.counties?.filter(c => normalizedActiveStates.includes(normalizeState(c.state || activeState)));
        //Append the state to the end of the keys for these objects, as county/region names can be shared between states
        countyToZip = Object.entries(countyToZip).reduce((acc, [key, value]) => {acc[`${key}:${activeState}`] = value; return acc;}, {} as CountyToZipMap);
        regionToCounty = Object.entries(regionToCounty).reduce((acc, [key, value]) => {acc[`${key}:${activeState}`] = value; return acc;}, {} as RegionToCountyMap);
        const secondState = normalizedActiveStates.find(s => s !== activeState);
        if (secondState !== undefined) {
            const normalizedSecondState = normalizeState(secondState);
            const data = getStateServiceAreaData(normalizedSecondState);
            countyToZip = Object.entries(data.countyToZip).reduce((acc, [key, value]) => {
                acc[`${key}:${normalizedSecondState}`] = value;
                return acc;
            }, countyToZip);
            regionToCounty = Object.entries(data.regionToCounty).reduce((acc, [key, value]) => {
                acc[`${key}:${normalizedSecondState}`] = value;
                return acc;
            }, regionToCounty);
            //I did not notice any key collisions with the zip code data
            zipToLatLng = {...zipToLatLng, ...data.zipToLatLng};
        }

    }
    const regions = buildRegions(regionToCounty, countyToZip, counties)
    useEffect(() => {
        ApiFetch<UserProviderPreferences>('User/ProviderPreferences')
            .then(setPreferences)
            // eslint-disable-next-line no-console
            .catch(console.log)
            .finally(() => setLoading(false))
    }, [])
    

    if (loading) return null
  
    //This form stores the service area as the Regions type, which is a Record<string, Record<string, string[]>>. regions[regionName][countyName] = list of zip codes in county
    //The back-end has no concept of regions, and instead uses County[] to store the service area. We'll have to convert between the two.
    const countiesToServiceArea = (counties: County[]) => {
        const isMultipleStates = Object.keys(countyToZip).find(k => k.includes(':')) !== undefined;
        const result = counties.reduce((serviceArea: Regions, county: County) => {
            const normalizedCountyState = normalizeState(county.state || activeState)
            const region = !isMultipleStates
            ? Object.entries(regionToCounty).find(e => e[1].includes(county.countyName))
            : Object.entries(regionToCounty).find(e => e[0].split(':')[1] === normalizedCountyState && e[1].includes(county.countyName))

            if (canonicalData && !region)
                return serviceArea;

            const regionName = region?.[0] || FALLBACK_REGION_NAME
            if (!serviceArea[regionName]) serviceArea[regionName] = {};
            const countyDefaults = !isMultipleStates
            ? countyToZip[county.countyName]?.map(v => v.toString()) || []
            : countyToZip[`${county.countyName}:${normalizedCountyState}`]?.map(v => v.toString()) || [];
            const currentCountyZips = getCountyZipList(county)
            if (!isMultipleStates)
                serviceArea[regionName][county.countyName] = currentCountyZips.length > 0 ? currentCountyZips : countyDefaults;
            else 
                serviceArea[regionName][`${county.countyName}:${normalizedCountyState}`] = currentCountyZips.length > 0 ? currentCountyZips : countyDefaults;
            return serviceArea;
        }, {} as Regions);
        return result;
    }

    const transformPreferencesPayload = (values: ProviderPreferencesForm) : ProviderPreferencesPayload => {
        const serviceRadius = values.serviceRadius ? parseInt(values.serviceRadius) : 0;
        //Converts the service area in preferences into County[] for the back-end.
        const serviceArea = values.serviceArea ?? {};
        let counties: County[] = [];
        const isMultipleStates = Object.keys(serviceArea).find(regionName => regionName.includes(':')) !== undefined;
        for (let regionName of Object.keys(serviceArea))
            for (let countyName of Object.keys(serviceArea[regionName])) {
                if (serviceArea[regionName][countyName].length === 0) continue;

                if (canonicalData && !(countyName in countyToZip))
                    continue;
                
                const county: County = !isMultipleStates 
                ? {countyName: countyName, state: activeState, zipCodes: serviceArea[regionName][countyName].join(','), zipCodesList: serviceArea[regionName][countyName]}
                : {countyName: countyName.split(':')[0], state: countyName.split(':')[1], zipCodes: serviceArea[regionName][countyName].join(','), zipCodesList: serviceArea[regionName][countyName]}
                if (countyToZip[countyName] && serviceArea[regionName][countyName].length === countyToZip[countyName].length)
                    delete county.zipCodes;
                counties.push(county);
        }
        
        //Might want to specifically set what values are sent in the payload later. For now, I'm sending everything but 
        //service area, so that saving preferences doesn't break if someone forgets to add a new field to this function.
        const valuesCopy = {...values};
        delete valuesCopy.serviceArea;
        // delete valuesCopy.serviceRadius;
        return {
            counties,
            ...valuesCopy
        }
    }

    function validateAdditionalContactsPreferences(values: ProviderPreferencesForm) {
    const errors: any = {};

    //  Validate additionalContacts
    if (Array.isArray(values.additionalContacts)) {
        errors.additionalContacts = [];

        values.additionalContacts.forEach((contact, idx) => {
        const contactErrors: any = {};

        // Full Name required
        if (!contact.fullName || contact.fullName.trim() === '') {
            contactErrors.fullName = 'Full Name is required';
        }

        // Communication methods logic
        const methods = values.communicationMethods || [];
        const needsEmail = methods.includes('Email');
        // Require phone if either 'Text Message' is selected
        const needsPhone = methods.includes('Text Message');

        if (needsEmail && (!contact.email || contact.email.trim() === '')) {
            contactErrors.email = 'Email is required';
        } else if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
            contactErrors.email = 'Invalid email';
        }

        if (needsPhone && (!contact.phoneNumber || contact.phoneNumber.trim() === '')) {
            contactErrors.phoneNumber = 'Phone Number is required';
        } else if (
            contact.phoneNumber &&
            !/^\(?\d{3}[) -.]*\d{3}[-.]?\d{4}$/.test(contact.phoneNumber)
        ) {
            contactErrors.phoneNumber = 'Invalid Phone ###-###-####';
        }

        errors.additionalContacts[idx] = contactErrors;
        });

        // Remove empty error objects
        if (errors.additionalContacts.every((e: any) => Object.keys(e).length === 0)) {
        delete errors.additionalContacts;
        }
    }

    return errors;
    }

    return (
        <Formik
            initialValues={{
                specialtyCategories: preferences?.specialtyCategories || [],
                serviceArea: counties ? countiesToServiceArea(counties) : {},
                serviceRadius: preferences?.serviceRadius !== undefined ? preferences.serviceRadius.toString() : '0',
                serviceRadiusZipCode: preferences?.serviceRadiusZipCode,
                communicationMethods: preferences?.communicationMethods || [],
                preferredPhoneNumber: preferences?.preferredPhoneNumber || '',
                additionalContacts: preferences?.additionalContacts?.length
                    ? preferences.additionalContacts
                    : [],
            } as ProviderPreferencesForm}
            validationSchema={validationScheme}
            validate={validateAdditionalContactsPreferences}
            onSubmit={(values, helpers) => {
                const payload = transformPreferencesPayload(values);
                return ApiPut('User/ProviderPreferences', undefined, payload)
                    .then((r) => {
                    cacheDelete && cacheDelete('ApiLookupSpecialtyBidOptions_isUserPreference');
                    user.providerCounties = payload.counties;
                    setUser({...user});
                    setPreferences({
                        ...preferences,
                        counties: payload.counties,
                        specialtyCategories: payload.specialtyCategories,
                        serviceArea: countiesToServiceArea(payload.counties? payload.counties: []),
                        serviceRadius: parseInt(payload.serviceRadius as unknown as string,10),
                        serviceRadiusZipCode: payload.serviceRadiusZipCode,
                        communicationMethods: values.communicationMethods,
                        preferredPhoneNumber: values.preferredPhoneNumber,
                        additionalContacts: values.additionalContacts
                    } as UserProviderPreferences);
                    helpers.setValues({
                        serviceArea: countiesToServiceArea(payload.counties!),
                        serviceRadius: payload.serviceRadius?.toString(),
                        serviceRadiusZipCode: payload.serviceRadiusZipCode,
                        specialtyCategories: payload.specialtyCategories,
                        communicationMethods: values.communicationMethods,
                        preferredPhoneNumber: values.preferredPhoneNumber,
                        additionalContacts: values.additionalContacts
                    });
                    helpers.setStatus({ 'success': r });
        })
        .catch(({ message: error }: Error) => helpers.setStatus({ error }));
    
            }}>
            {(form) => (
                <Grid className='preferences'>
                    <Cell small={12}>
                        <PageHeading pageTitle="My Preferences" createButton={false} filterButton={false} />
                        <Form
                            className='preferences-form'
                            onChange={() => form.setStatus(undefined)}
                        >
                            <BusinessInformation preferences={preferences} />
                            <ServiceTypes allowedSpecialtyCategories={preferences?.allowedSpecialtyCategories} />
                            <ProviderCommunicationPreferences />
                            <AdditionalNotificationContacts />
                            <ServiceArea states={activeStates ?? [activeState]} regions={regions} zipToLatLng={zipToLatLng} userZipCode={preferences?.zipCode}></ServiceArea>
                         
                            <FocusOnFirstError />

                            <SubmitMessage successMessage='Saved preferences' />

                            <div className='right standard-top-margin preferences-save-row'>
                                <TrackedButton event='Update'><SubmitButton text='Save' /></TrackedButton>
                            </div>
                        </Form>
                    </Cell>
                </Grid>
            )}
        </Formik>
    )
}

export default ProviderPreferenceForm;
